import { useTranslation } from 'react-i18next';
import { placesFor, type Position } from '../../entities/position';
import { cn } from '../../shared/lib/cn';
import { formatMoney, toNumber } from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { useElementWidth } from '../../shared/ui/chart/use-element-width';

const DOT: Record<string, string> = {
  filled: 'bg-text-1',
  triggered: 'bg-status-warning',
  pending: 'border border-border-strong bg-bg-1',
  skipped: 'bg-border-strong',
};

/** Minimum distance between the centres of two labels under the track. */
const LABEL_GAP_PX = 52;

/**
 * The ladder on one price axis: level N on the left, the target on the right (CLAUDE.md §5.2).
 */
export function LadderTrack({ position }: { position: Position }) {
  const { t } = useTranslation();
  const [ref, width] = useElementWidth();
  const locale = useLocale();
  const places = placesFor(position);
  const prices = position.levels.map((l) => toNumber(l.planned_price));
  const target = toNumber(position.holding.target_price);
  const current = position.quote ? toNumber(position.quote.price) : null;
  const min = Math.min(...prices, current ?? Infinity);
  const max = Math.max(target, ...prices, current ?? -Infinity);
  const span = max - min || 1;
  const fraction = (price: number): number => (price - min) / span;
  const at = (price: number): string => `${String(fraction(price) * 100)}%`;
  /** A level's label would overlap the target's (both sit under the track) when this close. */
  const clashesWithTarget = (price: number): boolean =>
    Math.abs(fraction(price) - fraction(target)) * width < LABEL_GAP_PX;

  const description = t('ladderTrack.label', {
    current: current === null ? '—' : formatMoney(current, locale, { places }),
    target: formatMoney(target, locale, { places }),
    filled: position.levels.filter((l) => l.status === 'filled').length,
    total: position.levels.length,
  });

  return (
    <div ref={ref} role="img" aria-label={description} className="relative mx-3 h-12">
      <div className="absolute top-5 right-0 left-0 h-px bg-border-strong" />
      {position.levels.map((level) => (
        <div
          key={level.n}
          className="absolute top-5 flex -translate-x-1/2 flex-col items-center"
          style={{ left: at(toNumber(level.planned_price)) }}
        >
          <span className={cn('-mt-1 size-2 rounded-full', DOT[level.status])} />
          <span
            className={cn(
              'mt-1 text-2xs text-text-3',
              clashesWithTarget(toNumber(level.planned_price)) && 'invisible',
            )}
          >
            {level.n === 0 ? t('ladder.entryShort') : `L${String(level.n)}`}
          </span>
        </div>
      ))}
      <div
        className="absolute top-3.5 flex -translate-x-1/2 flex-col items-center"
        style={{ left: at(target) }}
      >
        <span className="h-3 w-px bg-positive" />
        <span className="tabular mt-1 text-2xs text-positive">
          {formatMoney(target, locale, { places })}
        </span>
      </div>
      {current !== null && (
        <div
          className="absolute top-0 flex -translate-x-1/2 flex-col items-center"
          style={{ left: at(current) }}
        >
          <span className="tabular rounded-sm bg-bg-1 px-0.5 text-2xs font-medium text-text-1">
            {formatMoney(current, locale, { places })}
          </span>
          <span className="h-4 w-0.5 rounded-full bg-accent" />
        </div>
      )}
    </div>
  );
}
